import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usersApi } from '../api/users';
import PlayedEvents from '../components/PlayedEvents';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const API_URL = process.env.REACT_APP_API_URL ?? '';

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError('');
    try {
      await usersApi.uploadPhoto(file);
      await refreshUser();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } };
      setUploadError(error.response?.data?.detail ?? t('profile.uploadFailed'));
    } finally {
      setUploading(false);
    }
  }

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('profile.title')}</h1>
        <Link to="/profile/edit" className="btn btn-primary btn-sm">
          {t('profile.editButton')}
        </Link>
      </div>

      <div className="card">
        <div className="flex items-start gap-6 mb-6">
          <div className="flex-shrink-0">
            <div className="relative">
              {user.photo_url ? (
                <img
                  src={`${API_URL}${user.photo_url}`}
                  alt={t('nav.profilePhoto')}
                  className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-3xl font-bold border-2 border-gray-200">
                  {user.first_name[0]}{user.last_name[0]}
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute -bottom-1 -right-1 bg-white rounded-full p-1.5 shadow border border-gray-200 hover:bg-gray-50 transition-colors"
                title={t('profile.changePhotoTitle')}
              >
                {uploading ? (
                  <svg className="animate-spin w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhotoUpload}
            />
            {uploadError && <p className="text-red-500 text-xs mt-1">{uploadError}</p>}
          </div>

          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {user.first_name} {user.last_name}
            </h2>
            <p className="text-gray-500 mt-1">{user.email}</p>
            {user.level && (
              <span className="badge bg-primary-100 text-primary-700 mt-2">{user.level}</span>
            )}
          </div>
        </div>

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">{t('profile.email')}</dt>
            <dd className="mt-1 text-sm text-gray-900">{user.email}</dd>
          </div>
          {user.phone && (
            <div>
              <dt className="text-sm font-medium text-gray-500">{t('profile.phone')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{user.phone}</dd>
            </div>
          )}
          {user.level && (
            <div>
              <dt className="text-sm font-medium text-gray-500">{t('profile.level')}</dt>
              <dd className="mt-1 text-sm text-gray-900">{user.level}</dd>
            </div>
          )}
          <div>
            <dt className="text-sm font-medium text-gray-500">ELO</dt>
            <dd className="mt-1 flex items-center gap-2">
              <span className="text-xl font-bold text-gray-900">{(user as any).elo ?? 1000}</span>
              <span className={`badge border text-xs ${
                ((user as any).elo ?? 1000) >= 1150 ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                ((user as any).elo ?? 1000) >= 950  ? 'bg-blue-100 text-blue-800 border-blue-300' :
                'bg-gray-100 text-gray-600 border-gray-300'
              }`}>
                {((user as any).elo ?? 1000) >= 1150 ? 'A-Klasse' :
                 ((user as any).elo ?? 1000) >= 950  ? 'B-Klasse' : 'C-Klasse'}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t('profile.role')}</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {user.role === 'admin' ? t('profile.roleAdmin') : t('profile.rolePlayer')}
            </dd>
          </div>
          {user.preferred_times && (
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">{t('profile.preferredTimes')}</dt>
              <dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">{user.preferred_times}</dd>
            </div>
          )}
          {user.about_me && (
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">{t('profile.aboutMe')}</dt>
              <dd className="mt-1 text-sm text-gray-900 whitespace-pre-line">{user.about_me}</dd>
            </div>
          )}
          <div>
            <dt className="text-sm font-medium text-gray-500">{t('profile.memberSince')}</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {new Date(user.created_at).toLocaleDateString(getDateLocale())}
            </dd>
          </div>
        </dl>
      </div>

      <PlayedEvents userId={user.id} />
    </div>
  );
}
